<template>
  <div class="regulatory-intelligence">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="brand-header">
          <div class="brand-logo">
            <img 
              src="@/assets/accessiq-logo.png" 
              alt="AccessIQ" 
              class="accessiq-logo-img"
            />
          </div>
          <span class="brand-subtitle">Regulatory Compass</span>
        </div>
        <p class="page-description">
          Real-time healthcare regulatory intelligence. Track Medicaid policy changes, 
          monitor high-impact rules, and stay ahead of compliance requirements.
        </p>
      </div>
      <div class="header-actions">
        <div class="role-selector">
          <label class="role-label">View as:</label>
          <select v-model="selectedRole" @change="loadDashboard" class="role-dropdown">
            <option value="all">All Stakeholders</option>
            <option value="provider">Provider</option>
            <option value="mco">MCO</option>
            <option value="state">State Agency</option>
          </select>
        </div>
        <button @click="showFiltersModal = true" class="btn btn-outline-primary">
          <i class="fas fa-filter mr-2"></i>
          Filters
        </button>
        <router-link to="/policy-monitor" class="btn btn-secondary">
          <i class="fas fa-satellite-dish mr-2"></i>
          Policy Monitor
        </router-link>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fas fa-compass fa-spin fa-3x"></i>
        <p>Loading regulatory data...</p>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else class="dashboard-content">
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="card-icon new-changes">
            <i class="fas fa-bell"></i>
          </div>
          <div class="card-content">
            <span class="card-value">{{ summary.total_changes }}</span>
            <span class="card-label">New Changes</span>
            <span class="card-period">Last {{ summary.period_days }} days</span>
          </div>
        </div>
        
        <div class="summary-card">
          <div class="card-icon high-impact">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="card-content">
            <span class="card-value">{{ summary.high_impact }}</span>
            <span class="card-label">High-Impact</span>
            <span class="card-period">Requires attention</span>
          </div>
        </div>
        
        <div class="summary-card">
          <div class="card-icon upcoming">
            <i class="fas fa-calendar-alt"></i>
          </div>
          <div class="card-content">
            <span class="card-value">{{ summary.upcoming_effective }}</span>
            <span class="card-label">Upcoming Effective</span>
            <span class="card-period">Next 90 days</span>
          </div>
        </div>
        
        <div class="summary-card">
          <div class="card-icon programs">
            <i class="fas fa-project-diagram"></i>
          </div>
          <div class="card-content">
            <span class="card-value">{{ summary.programs_affected }}</span>
            <span class="card-label">Programs Affected</span>
            <span class="card-period">Active programs</span>
          </div>
        </div>
      </div>

      <!-- Data Sources Info -->
      <div v-if="dataSources && dataSources.total > 0" class="data-sources-bar">
        <div class="sources-info">
          <i class="fas fa-database"></i>
          <div class="sources-summary">
            <span class="sources-label">
              <strong>{{ dataSources.total.toLocaleString() }}</strong> subscribed sources
              <span v-if="dataSources.states_covered > 0"> across {{ dataSources.states_covered }} jurisdictions</span>
            </span>
            <div class="sources-stats">
              <span class="stat-pill stat-active">
                <i class="fas fa-circle"></i>
                {{ dataSources.with_events }} with data
              </span>
              <span class="stat-pill stat-pending">
                <i class="fas fa-clock"></i>
                {{ (dataSources.total - dataSources.with_events).toLocaleString() }} being indexed
              </span>
            </div>
            <div v-if="dataSources.top_sources && dataSources.top_sources.length > 0" class="top-sources">
              <span class="top-sources-label">Most active:</span>
              <span
                v-for="source in dataSources.top_sources"
                :key="source.id"
                class="source-tag"
                :title="`${source.events_count} events`"
              >
                {{ source.name }}
                <span class="source-count">({{ source.events_count }})</span>
              </span>
            </div>
          </div>
        </div>
        <div class="sources-actions">
          <router-link to="/source-catalog" class="browse-sources-link">
            <i class="fas fa-th-list"></i>
            Browse All Sources
          </router-link>
          <router-link to="/data-subscriptions" class="manage-sources-link">
            <i class="fas fa-cog"></i>
            Manage
          </router-link>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="main-grid">
        <!-- State Rankings Panel -->
        <div class="panel state-rankings-panel">
          <div class="panel-header">
            <h2 class="panel-title">
              <i class="fas fa-chart-bar mr-2"></i>
              Top States by Activity
            </h2>
          </div>
          <div class="panel-content">
            <div v-if="stateRankings.length === 0" class="empty-state-small">
              <i class="fas fa-map-marker-alt"></i>
              <p>No state data available</p>
            </div>
            <table v-else class="rankings-table">
              <thead>
                <tr>
                  <th>State</th>
                  <th class="text-center">Total</th>
                  <th class="text-center severity-col">High</th>
                  <th class="text-center severity-col">Med</th>
                  <th class="text-center severity-col">Low</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="state in stateRankings" :key="state.state">
                  <td class="state-cell">
                    <span class="state-code">{{ state.state }}</span>
                    <span class="state-name">{{ state.state_name }}</span>
                  </td>
                  <td class="text-center total-cell">{{ state.total }}</td>
                  <td class="text-center">
                    <span v-if="state.high > 0" class="severity-badge high">{{ state.high }}</span>
                    <span v-else class="severity-empty">-</span>
                  </td>
                  <td class="text-center">
                    <span v-if="state.medium > 0" class="severity-badge medium">{{ state.medium }}</span>
                    <span v-else class="severity-empty">-</span>
                  </td>
                  <td class="text-center">
                    <span v-if="state.low > 0" class="severity-badge low">{{ state.low }}</span>
                    <span v-else class="severity-empty">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Upcoming Effective Panel -->
        <div class="panel upcoming-panel">
          <div class="panel-header">
            <h2 class="panel-title">
              <i class="fas fa-clock mr-2"></i>
              Upcoming Effective Dates
            </h2>
          </div>
          <div class="panel-content">
            <div v-if="upcomingEffective.length === 0" class="empty-state-small">
              <i class="fas fa-calendar-check"></i>
              <p>No upcoming effective dates</p>
            </div>
            <div v-else class="upcoming-list">
              <div 
                v-for="item in upcomingEffective" 
                :key="item.id" 
                class="upcoming-item"
                :class="{ 'urgent': item.days_until_effective <= 7 }"
              >
                <div class="upcoming-countdown">
                  <span class="days-number">{{ item.days_until_effective }}</span>
                  <span class="days-label">days</span>
                </div>
                <div class="upcoming-details">
                  <span class="upcoming-state">{{ item.state }}</span>
                  <span class="upcoming-title">{{ truncateText(item.title, 50) }}</span>
                  <span class="upcoming-date">{{ formatDate(item.effective_date) }}</span>
                </div>
                <span 
                  class="severity-indicator" 
                  :class="item.impact_severity"
                  :title="item.impact_severity + ' impact'"
                ></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ranked Change Feed -->
      <div class="panel change-feed-panel">
        <div class="panel-header">
          <h2 class="panel-title">
            <i class="fas fa-stream mr-2"></i>
            Ranked Change Feed
          </h2>
          <div class="panel-actions">
            <span class="results-count">{{ recentChanges.length }} changes</span>
          </div>
        </div>
        <div class="panel-content">
          <div v-if="recentChanges.length === 0" class="empty-state">
            <i class="fas fa-inbox fa-3x"></i>
            <h3>No Recent Changes</h3>
            <p>Configure your policy sources to start tracking regulatory changes.</p>
            <router-link to="/source-catalog" class="btn btn-primary">
              <i class="fas fa-plus mr-2"></i>
              Add Sources
            </router-link>
          </div>
          <div v-else class="change-feed">
            <div 
              v-for="change in recentChanges" 
              :key="change.id" 
              class="change-item"
              :class="'severity-' + change.impact_severity"
            >
              <div class="change-severity-bar" :class="change.impact_severity"></div>
              <div class="change-content">
                <div class="change-header">
                  <span class="change-state-badge">{{ change.state || 'FED' }}</span>
                  <span class="change-type">{{ change.item_type_display }}</span>
                  <span class="change-severity-badge" :class="change.impact_severity">
                    {{ change.impact_severity }}
                  </span>
                  <span v-if="change.source_type === 'global'" class="source-type-badge global" title="Official Federal Register">
                    <i class="fas fa-landmark"></i>
                  </span>
                  <span v-if="change.effective_date" class="change-effective">
                    <i class="fas fa-calendar-alt"></i>
                    {{ formatDate(change.effective_date) }}
                  </span>
                </div>
                <h3 class="change-title">{{ change.title }}</h3>
                <p class="change-summary">{{ change.summary || change.what_changed }}</p>
                <div class="change-footer">
                  <div class="change-affects">
                    <span v-if="change.affects_providers" class="affects-tag" title="Affects Providers">
                      <i class="fas fa-user-md"></i> Providers
                    </span>
                    <span v-if="change.affects_mcos" class="affects-tag" title="Affects MCOs">
                      <i class="fas fa-building"></i> MCOs
                    </span>
                  </div>
                  <div class="change-programs" v-if="change.programs_affected.length > 0">
                    <span 
                      v-for="program in change.programs_affected.slice(0, 3)" 
                      :key="program" 
                      class="program-tag"
                    >
                      {{ program }}
                    </span>
                    <span v-if="change.programs_affected.length > 3" class="more-programs">
                      +{{ change.programs_affected.length - 3 }}
                    </span>
                  </div>
                  <a 
                    v-if="change.source_url" 
                    :href="change.source_url" 
                    target="_blank" 
                    class="source-link"
                  >
                    <i class="fas fa-external-link-alt"></i>
                    View Source
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Modal -->
    <div v-if="showFiltersModal" class="modal-overlay" @click.self="showFiltersModal = false">
      <div class="modal-content filters-modal">
        <div class="modal-header">
          <h2><i class="fas fa-filter mr-2"></i>Report Builder</h2>
          <button @click="showFiltersModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="filter-section">
            <label>Time Period</label>
            <select v-model="filters.days" class="filter-select">
              <option :value="30">Last 30 days</option>
              <option :value="60">Last 60 days</option>
              <option :value="90">Last 90 days</option>
              <option :value="180">Last 6 months</option>
              <option :value="365">Last year</option>
            </select>
          </div>
          <div class="filter-section">
            <label>Impact Severity</label>
            <div class="checkbox-group">
              <label class="checkbox-item">
                <input type="checkbox" v-model="filters.severity" value="high">
                <span class="severity-badge high">High</span>
              </label>
              <label class="checkbox-item">
                <input type="checkbox" v-model="filters.severity" value="medium">
                <span class="severity-badge medium">Medium</span>
              </label>
              <label class="checkbox-item">
                <input type="checkbox" v-model="filters.severity" value="low">
                <span class="severity-badge low">Low</span>
              </label>
            </div>
          </div>
          <div class="filter-section">
            <label>Programs</label>
            <div class="checkbox-group programs-list">
              <label 
                v-for="program in programsList" 
                :key="program" 
                class="checkbox-item"
              >
                <input type="checkbox" v-model="filters.programs" :value="program">
                <span>{{ program }}</span>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="resetFilters" class="btn btn-secondary">
            <i class="fas fa-undo mr-2"></i>
            Reset
          </button>
          <button @click="applyFilters" class="btn btn-primary">
            <i class="fas fa-check mr-2"></i>
            Apply Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Regulatory Agent Chat -->
    <RegulatoryAgentChat
      :summary="summary"
      :filters="filters"
      @filter-events="handleAgentFilter"
      @show-events-list="handleShowEventsList"
      @show-event-detail="handleShowEventDetail"
    />

    <!-- Events List Modal (from agent) -->
    <div v-if="showEventsListModal" class="modal-overlay" @click.self="showEventsListModal = false">
      <div class="modal-content events-modal">
        <div class="modal-header">
          <h2>{{ eventsListData.title || 'Policy Events' }}</h2>
          <button @click="showEventsListModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p v-if="eventsListData.subtitle" class="events-subtitle">{{ eventsListData.subtitle }}</p>
          <div v-if="eventsListData.events?.length === 0" class="empty-state-small">
            <i class="fas fa-search"></i>
            <p>No events found</p>
          </div>
          <div v-else class="events-list">
            <div 
              v-for="event in eventsListData.events" 
              :key="event.id" 
              class="event-item"
              @click="viewEventDetail(event)"
            >
              <div class="event-item-header">
                <span v-if="event.state" class="event-state-tag">{{ event.state }}</span>
                <span class="event-severity-badge" :class="(event.severity || '').toLowerCase()">
                  {{ event.severity || 'Unknown' }}
                </span>
              </div>
              <div class="event-item-title">{{ event.title }}</div>
              <div class="event-item-meta">
                <span v-if="event.effective_date">
                  <i class="fas fa-calendar-alt"></i> {{ formatDate(event.effective_date) }}
                </span>
                <span v-if="event.item_type">
                  <i class="fas fa-tag"></i> {{ event.item_type }}
                </span>
              </div>
              <p v-if="event.summary" class="event-item-summary">{{ event.summary }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import RegulatoryAgentChat from '@/components/regulatory/RegulatoryAgentChat.vue';

export default {
  name: 'RegulatoryIntelligence',
  
  components: {
    RegulatoryAgentChat
  },
  
  data() {
    return {
      loading: true,
      selectedRole: 'all',
      showFiltersModal: false,
      showEventsListModal: false,
      eventsListData: {
        title: '',
        subtitle: '',
        events: []
      },
      
      // Dashboard data
      summary: {
        total_changes: 0,
        high_impact: 0,
        upcoming_effective: 0,
        programs_affected: 0,
        period_days: 90,
        global_events: 0,
        org_events: 0,
        subscriptions_active: 0
      },
      stateRankings: [],
      recentChanges: [],
      upcomingEffective: [],
      programsList: [],
      dataSources: null,
      
      // Filters
      filters: {
        days: 90,
        severity: ['high', 'medium', 'low'],
        programs: [],
        states: []
      }
    };
  },
  
  mounted() {
    this.loadDashboard();
  },
  
  methods: {
    async loadDashboard() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        params.append('role', this.selectedRole);
        params.append('days', this.filters.days);
        
        if (this.filters.states.length > 0) {
          params.append('states', this.filters.states.join(','));
        }
        
        const response = await api.get(`/regulatory/dashboard/?${params.toString()}`);
        const data = response.data;
        
        this.summary = data.summary;
        this.stateRankings = data.state_rankings;
        this.recentChanges = data.recent_changes;
        this.upcomingEffective = data.upcoming_effective;
        this.programsList = data.programs_list || [];
        this.dataSources = data.data_sources || [];
        
      } catch (error) {
        console.error('Failed to load regulatory dashboard:', error);
        this.$toast?.error?.('Failed to load dashboard data');
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    
    resetFilters() {
      this.filters = {
        days: 90,
        severity: ['high', 'medium', 'low'],
        programs: [],
        states: []
      };
    },
    
    applyFilters() {
      this.showFiltersModal = false;
      this.loadDashboard();
    },
    
    // Agent chat handlers
    handleAgentFilter(filterData) {
      // Apply filters from agent
      if (filterData.states) {
        this.filters.states = filterData.states;
      }
      if (filterData.severity) {
        this.filters.severity = Array.isArray(filterData.severity) 
          ? filterData.severity 
          : [filterData.severity];
      }
      if (filterData.programs) {
        this.filters.programs = filterData.programs;
      }
      this.loadDashboard();
    },
    
    handleShowEventsList(data) {
      this.eventsListData = {
        title: data.title || 'Policy Events',
        subtitle: data.subtitle || '',
        events: data.events || []
      };
      this.showEventsListModal = true;
    },
    
    handleShowEventDetail(event) {
      // For now, open in a modal or navigate - you can expand this
      // to show a detailed view modal
      if (event.source_url) {
        window.open(event.source_url, '_blank');
      }
    },
    
    viewEventDetail(event) {
      if (event.source_url) {
        window.open(event.source_url, '_blank');
      }
    }
  }
};
</script>

<style scoped>
.regulatory-intelligence {
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
  background: #f8fafc;
  min-height: 100vh;
}

/* Header */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.brand-logo {
  display: flex;
  align-items: center;
}

.accessiq-logo-img {
  height: 48px;
  width: auto;
}

.brand-subtitle {
  font-size: 18px;
  color: #64748b;
  font-weight: 500;
}

.page-description {
  color: #64748b;
  margin: 0;
  max-width: 600px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.role-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.role-label {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.role-dropdown {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #334155;
  cursor: pointer;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  text-align: center;
  color: #64748b;
}

.loading-spinner i {
  color: #3b82f6;
  margin-bottom: 16px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.card-icon.new-changes {
  background: #ccfbf1;
  color: #0d9488;
}

.card-icon.high-impact {
  background: #fef2f2;
  color: #dc2626;
}

.card-icon.upcoming {
  background: #fef3c7;
  color: #d97706;
}

.card-icon.programs {
  background: #d1fae5;
  color: #059669;
}

.card-content {
  display: flex;
  flex-direction: column;
}

.card-value {
  font-size: 32px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1;
}

.card-label {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-top: 4px;
}

.card-period {
  font-size: 12px;
  color: #94a3b8;
}

/* Main Grid */
.main-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

/* Panels */
.panel {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.panel-title i {
  color: #64748b;
}

.panel-content {
  padding: 16px 20px;
}

/* State Rankings Table */
.rankings-table {
  width: 100%;
  border-collapse: collapse;
}

.rankings-table th,
.rankings-table td {
  padding: 10px 8px;
  text-align: left;
}

.rankings-table th {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
}

.rankings-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
}

.rankings-table tbody tr:last-child {
  border-bottom: none;
}

.rankings-table tbody tr:hover {
  background: #f8fafc;
}

.state-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.state-code {
  font-weight: 600;
  color: #0f172a;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.state-name {
  font-size: 13px;
  color: #64748b;
}

.total-cell {
  font-weight: 600;
  color: #0f172a;
}

.severity-col {
  width: 50px;
}

.severity-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}

.severity-badge.high {
  background: #fef2f2;
  color: #dc2626;
}

.severity-badge.medium {
  background: #fef3c7;
  color: #d97706;
}

.severity-badge.low {
  background: #d1fae5;
  color: #059669;
}

.severity-empty {
  color: #cbd5e1;
}

/* Upcoming List */
.upcoming-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.upcoming-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #e2e8f0;
}

.upcoming-item.urgent {
  border-left-color: #dc2626;
  background: #fef2f2;
}

.upcoming-countdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 50px;
}

.days-number {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1;
}

.upcoming-item.urgent .days-number {
  color: #dc2626;
}

.days-label {
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
}

.upcoming-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.upcoming-state {
  font-size: 11px;
  font-weight: 600;
  color: #0d9488;
  text-transform: uppercase;
}

.upcoming-title {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
}

.upcoming-date {
  font-size: 12px;
  color: #94a3b8;
}

.severity-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.severity-indicator.high { background: #dc2626; }
.severity-indicator.medium { background: #d97706; }
.severity-indicator.low { background: #059669; }

/* Change Feed */
.change-feed-panel {
  grid-column: 1 / -1;
}

.change-feed {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.change-item {
  display: flex;
  background: #f8fafc;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.change-item:hover {
  border-color: #cbd5e1;
}

.change-severity-bar {
  width: 4px;
  flex-shrink: 0;
}

.change-severity-bar.high { background: #dc2626; }
.change-severity-bar.medium { background: #d97706; }
.change-severity-bar.low { background: #059669; }

.change-content {
  flex: 1;
  padding: 16px;
}

.change-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.change-state-badge {
  background: #0d9488;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.change-type {
  font-size: 12px;
  color: #64748b;
}

.change-severity-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.change-effective {
  font-size: 12px;
  color: #64748b;
  margin-left: auto;
}

.change-effective i {
  margin-right: 4px;
}

.change-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.change-summary {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.change-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.change-affects {
  display: flex;
  gap: 8px;
}

.affects-tag {
  font-size: 11px;
  color: #64748b;
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.affects-tag i {
  margin-right: 4px;
}

.change-programs {
  display: flex;
  gap: 6px;
}

.program-tag {
  font-size: 11px;
  background: #ccfbf1;
  color: #0d9488;
  padding: 2px 8px;
  border-radius: 4px;
}

.more-programs {
  font-size: 11px;
  color: #64748b;
}

.source-link {
  font-size: 12px;
  color: #0d9488;
  text-decoration: none;
  margin-left: auto;
}

.source-link:hover {
  color: #0f766e;
  text-decoration: underline;
}

/* Empty States */
.empty-state {
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
}

.empty-state i {
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  color: #334155;
  margin: 0 0 8px 0;
}

.empty-state p {
  margin: 0 0 16px 0;
}

.empty-state-small {
  text-align: center;
  padding: 24px;
  color: #94a3b8;
}

.empty-state-small i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

.empty-state-small p {
  margin: 0;
  font-size: 13px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 18px;
  margin: 0;
  color: #0f172a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #64748b;
  cursor: pointer;
}

.close-btn:hover {
  color: #0f172a;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-section label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.filter-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.checkbox-item input {
  cursor: pointer;
}

.programs-list {
  max-height: 150px;
  overflow-y: auto;
  flex-direction: column;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  border: none;
  transition: all 0.2s;
}

.btn-primary {
  background: #0d9488;
  color: white;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-outline-primary {
  background: white;
  color: #0d9488;
  border: 1px solid #0d9488;
}

.btn-outline-primary:hover {
  background: #f0fdfa;
}

/* Responsive */
@media (max-width: 1200px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .main-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 16px;
  }
  
  .header-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .summary-cards {
    grid-template-columns: 1fr;
  }
  
  .change-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .change-effective {
    margin-left: 0;
  }
}

.results-count {
  font-size: 13px;
  color: #64748b;
}

/* Data Sources Bar */
.data-sources-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%);
  border: 1px solid #99f6e4;
  border-radius: 8px;
  margin-bottom: 24px;
}

.sources-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sources-info > i {
  color: #0d9488;
  font-size: 16px;
}

.sources-label {
  font-size: 13px;
  color: #0f766e;
  font-weight: 500;
}

.sources-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sources-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.stat-pill i {
  font-size: 8px;
}

.stat-active {
  background: #dcfce7;
  color: #166534;
}

.stat-pending {
  background: #fef9c3;
  color: #854d0e;
}

.top-sources {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.top-sources-label {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.source-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.source-tag {
  font-size: 12px;
  background: white;
  color: #0d9488;
  padding: 3px 10px;
  border-radius: 16px;
  border: 1px solid #99f6e4;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.source-count {
  font-size: 11px;
  color: #64748b;
}

.sources-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.browse-sources-link {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #0d9488;
  text-decoration: none;
  white-space: nowrap;
  font-weight: 500;
}

.browse-sources-link:hover {
  color: #0f766e;
  text-decoration: underline;
}

.manage-sources-link {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #0d9488;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s;
}

.manage-sources-link:hover {
  background: white;
  color: #0f766e;
}

/* Source Type Badge */
.source-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 10px;
}

.source-type-badge.global {
  background: #dbeafe;
  color: #1d4ed8;
}

/* Events Modal from Agent */
.events-modal {
  max-width: 700px;
  max-height: 80vh;
}

.events-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0 0 16px 0;
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.event-item:hover {
  background: #f0fdfa;
  border-color: #99f6e4;
}

.event-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.event-state-tag {
  background: #e0f2fe;
  color: #0369a1;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.event-severity-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
}

.event-severity-badge.high {
  background: #fee2e2;
  color: #991b1b;
}

.event-severity-badge.medium {
  background: #fef3c7;
  color: #92400e;
}

.event-severity-badge.low {
  background: #dcfce7;
  color: #166534;
}

.event-item-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
  line-height: 1.4;
}

.event-item-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
}

.event-item-meta i {
  margin-right: 4px;
}

.event-item-summary {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin: 0;
}
</style>
